import { useState } from 'react';
import { FileText, X, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';

const SOURCE_TYPES = [
  { value: 'state_health', label: 'State Health', color: 'hsl(224, 72%, 29%)' },
  { value: 'federal', label: 'Federal', color: 'hsl(var(--primary))' },
  { value: 'accreditation', label: 'Accreditation', color: 'hsl(var(--muted-foreground))' },
  { value: 'existing_policy', label: 'Existing Policy', color: 'hsl(var(--status-approved))' },
] as const;

interface SourceCardProps {
  id: string;
  fileName: string;
  sourceType: string;
  processingStatus: string;
  filePath: string;
  onDeleted: (id: string) => void;
}

export function SourceCard({ id, fileName, sourceType, processingStatus, filePath, onDeleted }: SourceCardProps) {
  const [checked, setChecked] = useState(processingStatus === 'ready');
  const [type, setType] = useState(sourceType);
  const [deleting, setDeleting] = useState(false);

  const typeInfo = SOURCE_TYPES.find((t) => t.value === type) || SOURCE_TYPES[1];

  const handleTypeChange = async (val: string) => {
    setType(val);
    await supabase.from('regulatory_documents').update({ source_type: val }).eq('id', id);
  };

  const handleDelete = async () => {
    setDeleting(true);
    await supabase.storage.from('regulatory-library').remove([filePath]);
    await supabase.from('regulatory_documents').delete().eq('id', id);
    onDeleted(id);
  };

  const isReady = processingStatus === 'ready';
  const isError = processingStatus === 'error';
  const isProcessing = !isReady && !isError;

  return (
    <div
      className={`group relative flex items-start gap-2 p-3 rounded-md border border-border bg-card transition-opacity ${
        !checked ? 'opacity-50' : ''
      }`}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={(v) => setChecked(!!v)}
        disabled={!isReady}
        className="mt-0.5 flex-shrink-0"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm font-medium truncate">{fileName}</span>
        </div>

        <div className="flex items-center gap-2 mt-1.5">
          <Select value={type} onValueChange={handleTypeChange}>
            <SelectTrigger className="h-6 text-[10px] font-medium border-0 px-2 py-0 w-auto gap-1" style={{ backgroundColor: typeInfo.color, color: '#fff' }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCE_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value} className="text-xs">
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status */}
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            {isProcessing && (
              <>
                <Loader2 className="h-3 w-3 animate-spin" /> Processing...
              </>
            )}
            {isReady && (
              <>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: 'hsl(var(--status-approved))' }} />
                Ready
              </>
            )}
            {isError && (
              <>
                <span className="h-2 w-2 rounded-full bg-destructive" />
                Error
              </>
            )}
          </span>
        </div>
      </div>

      {/* Delete */}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive p-0.5"
            disabled={deleting}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this source?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove the document from this session.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
